import styled from 'styled-components';
import { BubbleGrey } from '../../components/shared/Bubble';
import { FlexCol, FlexRowMiddle } from '../../components/shared/Flex';
import {
	panelHeaderTop,
	Table,
	TableBody,
	TableHead,
	tablePaddingSide,
	TableRow,
	TD,
	TH,
} from '../../components/shared/Table';
import { Text } from '../../components/shared/Text';
import { EditCell } from '../../components/shared/EditCell';
import {
	checkCurrencyFormatFull,
	checkCurrencyFormatPartly,
	convertObjectToOptions,
	formatCurrency,
	getOptionList,
} from '../../scripts/scripts';
import BigNumber from 'bignumber.js';
import { EditCellCurrency } from '../../components/shared/EditCellCurrency';
import {
	TixBankTypes,
	TixBought,
	TixCurrencyTypes,
	TixEvent,
	TixSecondaryName,
	TixSecondaryTypes,
} from '../../types/TixTypes';
import { Icon } from '../../components/shared/Icon';
import IconSeat from '../../../public/icons/seat.svg?react';
import { useLoadingStore } from '../../store/useLoadingStore';
import { Checkbox } from '../../components/shared/Checkbox';
import { useSelectionStore } from '../../store/useSelectionStore';
import { ButtonGrey, ButtonRed } from '../../components/shared/Buttons';
import { OnClickEditOrderModal } from '../../components/modals/ModalEditOrder';
import { EditOrderAPIFunc } from '../../scripts/hooks/useEditOrderAPI';
import { ReactNode, useState } from 'react';
import IconSeatAisle from '../../../public/icons/seat_aisle.svg?react';
import IconSeatRV from '../../../public/icons/seat_rv.svg?react';
import IconSeatSide from '../../../public/icons/seat_side.svg?react';
import { EditCellSelectMulti } from '../../components/shared/EditCellSelectMulti';
import { EditCellCheckbox } from '../../components/shared/EditCellCheckbox';
import IconCheckmark from '../../../public/icons/checkmark.svg?react';
import IconCross from '../../../public/icons/close.svg?react';
import { GETOrdersResponseEventBASE } from '../../types/TixAPITypes';
import { OSStatsHeader, OSHeaderTitle } from '../../components/orders-sales/OSStatsHeader';
import { OSStatsHeaderMobile } from '../../components/orders-sales/OSStatsHeaderMobile';
import { OSToggle } from '../../components/orders-sales/OSToggle';
import { Breakpoints } from '../../components/CSSHelper';

const orderColumnArray = [
	{ id: 'checkbox_1', width: '40px', header: '' },
	{ id: 'listed', width: '150px', header: 'Exchanges' },
	{ id: 'preupload', width: '74px', header: 'Pre-up' },
	{ id: 'site', width: '100px', header: 'Site' },
	{ id: 'section', width: '140px', header: 'Section' },
	{ id: 'row', width: '60px', header: 'Row' },
	{ id: 'seat', width: '60px', header: 'Seat' },
	{ id: 'note', width: 'minmax(150px, 1fr)', header: 'Notes' },
	{ id: 'cost', width: '150px', header: 'Cost' },
	{ id: 'order_id', width: '175px', header: 'Order ID' },
	{ id: 'account', width: '200px', header: 'Account' },
	{ id: 'bank', width: '175px', header: 'Bank', headerStyle: { justifyContent: 'center' } },
	{ id: 'payment', width: '150px', header: 'Payment' },
	{ id: 'payment_native', width: '150px', header: 'Payment Native' },
	{ id: 'payment_date', width: 'minmax(100px, 1fr)', header: 'Payment Date' },
	{ id: 'checkbox_2', width: '150px', header: '' },
];

const orderColumns = orderColumnArray.map((c) => c.width).join(' ');

const StyledPanelHeader = styled(FlexRowMiddle)`
	padding: ${panelHeaderTop} ${tablePaddingSide};
	display: grid;
	grid-template-columns: 30px minmax(200px, 1fr) auto minmax(0, 600px);
	column-gap: 2rem;
	background-color: ${({ theme }) => theme.panel_highlight_bg};
	border-bottom: 1px solid ${({ theme }) => theme.panel_main_border};
	.eventStatsMobile {
		display: none;
	}
	@media (max-width: ${Breakpoints.TABLET}) {
		grid-template-columns: 30px minmax(200px, 1fr) auto;
		grid-template-rows: auto 40px;
		row-gap: 1.5rem;
		.eventStatsDesktop {
			display: none;
		}
		.eventStatsMobile {
			display: grid;
			grid-column: 1 / span 3;
		}
	}
`;

const StyledEventButtonContainer = styled(FlexCol)`
	row-gap: 0.5rem;
	width: 105px;
	button {
		width: 100%;
	}
`;

export function OrderPanel({
	event,
	editOrderAPI,
	onAddFine,
	onClickEditOrder,
}: {
	event: GETOrdersResponseEventBASE;
	editOrderAPI: EditOrderAPIFunc;
	onAddFine: (eventId: TixEvent['id']) => void;
	onClickEditOrder: OnClickEditOrderModal;
}) {
	const { selectedSeatIds: ordersSelected } = useSelectionStore();

	const [showSeats, setShowSeats] = useState<boolean>(false);

	const nOrdersSelected = Object.entries(ordersSelected).filter((idPair) => idPair[1]);

	const seatsArray = Object.values(event.seats).filter((seat) => !event?.seatIdsSold?.includes(seat.id));

	return (
		<>
			<StyledPanelHeader>
				<OSToggle active={showSeats} setActive={setShowSeats} />
				<OSHeaderTitle>
					<Text $color="text_title_main" $size="1.8rem" $tabletSize="1.5rem" $weight="600">
						{event.event}
					</Text>
					<Text $color="text_title_alt" $size="1.3rem" $tabletSize="1.2rem" $weight="400">
						{event.date} - {event.city}
					</Text>
				</OSHeaderTitle>

				<StyledEventButtonContainer style={{ rowGap: '0.5rem', marginLeft: 'auto' }}>
					<ButtonRed $small={true} onClick={() => onAddFine(event.id)} disabled={nOrdersSelected.length > 0}>
						Add Fine
					</ButtonRed>
				</StyledEventButtonContainer>
				<OSStatsHeader stats={event.stats} showToBeSold={true} />
				<OSStatsHeaderMobile stats={event.stats} showToBeSold={true} />
			</StyledPanelHeader>
			{showSeats && (
				<Table>
					<TableHead columns={orderColumns}>
						{orderColumnArray.map((c) => (
							<TH style={c.headerStyle ? c.headerStyle : undefined}>{c.header}</TH>
						))}
					</TableHead>
					<TableBody>
						{seatsArray.map((seat) => {
							return (
								<OrderRow
									event={event}
									seat={seat}
									key={seat.id}
									editOrderAPI={editOrderAPI}
									onClickEditOrder={onClickEditOrder}
								/>
							);
						})}
					</TableBody>
				</Table>
			)}
		</>
	);
}

function OrderRow({
	event,
	seat,
	editOrderAPI,
	onClickEditOrder,
}: {
	event: GETOrdersResponseEventBASE;
	seat: TixBought;
	editOrderAPI: EditOrderAPIFunc;
	onClickEditOrder: OnClickEditOrderModal;
}) {
	const { toggleSelection, selectedSeatIds: selectedOrderIds, selectedEventId } = useSelectionStore();
	const { orderIdsLoading } = useLoadingStore();
	const order = event.orders[seat.orderId];

	const savePaymentTotal = async (amount: string) => {
		const newTotal = !amount ? null : new BigNumber(amount).toNumber();
		await editOrderAPI({
			orderId: seat.orderId,
			originalEvent: event,
			updatedOrder: {
				paymentTotal: newTotal,
				paymentSplits: [{ payment: newTotal, bank: order.paymentSplits[0]?.bank ?? TixBankTypes.UNKNOWN }],
			},
		});
	};

	const savePaymentNative = async (amount: string, currency: string) => {
		await editOrderAPI({
			orderId: seat.orderId,
			originalEvent: event,
			updatedOrder: {
				paymentNative: !amount ? null : new BigNumber(amount).toNumber(),
				paymentNativeCurrency: currency === '' ? null : (currency as TixCurrencyTypes),
			},
		});
	};

	const saveListed = async (exchanges: string[]) => {
		await editOrderAPI({
			orderId: seat.orderId,
			originalEvent: event,
			updatedSeats: [
				{
					...seat,
					listedExchanges: exchanges.filter((v) => v !== '') as TixSecondaryTypes[],
				},
			],
		});
	};

	const savePreuploaded = async (preuploaded: boolean | null) => {
		await editOrderAPI({
			orderId: seat.orderId,
			originalEvent: event,
			updatedSeats: [
				{
					...seat,
					preuploaded: preuploaded ? true : false,
				},
			],
		});
	};

	const ticketPreuploaded = seat.preuploaded ?? false;

	const cellContent: Record<string, ReactNode> = {
		checkbox_1: (
			<Checkbox
				disabled={selectedEventId !== undefined && selectedEventId !== order.eventId}
				onChange={() => toggleSelection(order.eventId, seat.id)}
				value={selectedOrderIds[seat.id]}
			/>
		),
		listed: (
			<EditCellSelectMulti
				value={seat.listedExchanges?.length ? seat.listedExchanges : ['']}
				saveValues={saveListed}
				options={getOptionList('Exchange', convertObjectToOptions(TixSecondaryName))}
				altColor={!seat.listedExchanges?.length}
			>
				{!seat.listedExchanges?.length
					? '- None -'
					: seat.listedExchanges.map((exchange) => TixSecondaryName[exchange]).join(', ')}
			</EditCellSelectMulti>
		),
		preupload: (
			<EditCellCheckbox value={ticketPreuploaded} saveValues={savePreuploaded}>
				<Icon $colorFill={ticketPreuploaded ? 'text_theme' : 'text_desc_alt'} style={{ height: 12 }}>
					{ticketPreuploaded ? <IconCheckmark /> : <IconCross />}
				</Icon>
			</EditCellCheckbox>
		),
		site: <BubbleGrey>{order.primary}</BubbleGrey>,
		account: (
			<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
				{order.email}
			</Text>
		),
		cost: (
			<>
				<Icon $colorFill="text_desc_alt" style={{ height: 14 }}>
					<IconSeat />
				</Icon>
				<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
					{seat.price === null ? 'N/A' : formatCurrency(seat.price, 'EUR')}
				</Text>
			</>
		),
		section: (
			<Text $color="text_desc_main" $size="1.3rem" $weight="400">
				{seat.section}
			</Text>
		),
		row: (
			<Text $color="text_desc_main" $size="1.3rem" $weight="400">
				{seat.row}
			</Text>
		),
		seat: (
			<Text $color="text_desc_main" $size="1.3rem" $weight="400">
				{seat.seat}
			</Text>
		),
		note:
			seat.noteAisle || seat.noteRV || seat.noteSide || seat.note ? (
				<FlexRowMiddle style={{ columnGap: '1rem' }}>
					{seat.noteAisle && (
						<Icon $colorFill="text_theme" style={{ height: 12, width: 12 }}>
							<IconSeatAisle />
						</Icon>
					)}
					{seat.noteRV && (
						<Icon $colorFill="text_orange" style={{ height: 12, width: 12 }}>
							<IconSeatRV />
						</Icon>
					)}
					{seat.noteSide && (
						<Icon $colorFill="text_red" style={{ height: 12, width: 12 }}>
							<IconSeatSide />
						</Icon>
					)}
					{seat.note && (
						<Text $color="text_desc_alt" $size="1.1rem" $weight="400">
							{seat.note}
						</Text>
					)}
				</FlexRowMiddle>
			) : (
				<Text $color="text_desc_alt" $size="1.1rem" $weight="400" style={{}}>
					- None -
				</Text>
			),
		bank: (
			<FlexCol style={{ rowGap: '0.5rem' }}>
				{order.paymentSplits.map((split, index) => (
					<BubbleGrey key={`bubble_bank_${index}_${seat.id}`}>{split.bank}</BubbleGrey>
				))}
			</FlexCol>
		),
		payment: (
			<EditCell
				value={(order.paymentTotal ?? '').toString()}
				saveValue={savePaymentTotal}
				onInputMap={checkCurrencyFormatPartly}
				onBlurMap={checkCurrencyFormatFull}
				disabled={orderIdsLoading[seat.orderId] || order.paymentSplits.length > 1}
			>
				{order.paymentTotal === null ? 'N/A' : formatCurrency(order.paymentTotal, 'EUR')}
			</EditCell>
		),
		payment_native: (
			<EditCellCurrency
				value={(order.paymentNative ?? '').toString()}
				saveValues={savePaymentNative}
				valueSelect={order.paymentNativeCurrency ?? ''}
				options={[
					{ value: '', text: 'N/A' },
					{ value: 'GBP', text: 'GBP' },
					{ value: 'USD', text: 'USD' },
					{ value: 'PLN', text: 'PLN' },
					{ value: 'CAD', text: 'CAD' },
					{ value: 'MXN', text: 'MXN' },
					{ value: 'RON', text: 'RON' },
					{ value: 'HUF', text: 'HUF' },
					{ value: 'CHF', text: 'CHF' },
				]}
				onInputMap={checkCurrencyFormatPartly}
				onBlurMap={checkCurrencyFormatFull}
				disabled={orderIdsLoading[seat.orderId]}
			>
				{order.paymentNative && order.paymentNativeCurrency
					? formatCurrency(order.paymentNative, order.paymentNativeCurrency)
					: 'N/A'}
			</EditCellCurrency>
		),
		order_id: (
			<>
				<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
					{order.reference}
				</Text>
				<BubbleGrey>
					<FlexRowMiddle>
						<Icon $colorFill="bubble_grey_fg" style={{ marginRight: '0.5rem', height: 12 }}>
							<IconSeat />
						</Icon>
						{order.quantity}
					</FlexRowMiddle>
				</BubbleGrey>
			</>
		),
		payment_date: (
			<Text $color="text_desc_alt" $size="1.3rem" $weight="400">
				{order.paymentDate}
			</Text>
		),
		checkbox_2: (
			<>
				<Checkbox
					disabled={selectedEventId !== undefined && selectedEventId !== order.eventId}
					onChange={() => toggleSelection(order.eventId, seat.id)}
					value={selectedOrderIds[seat.id]}
				/>
				<ButtonGrey onClick={() => onClickEditOrder({ orderId: seat.orderId, event: event })}>
					Edit Order
				</ButtonGrey>
			</>
		),
	};

	return (
		<TableRow $columns={orderColumns} key={seat.id} $highlighted={selectedOrderIds[seat.id]} $hoverEnabled={true}>
			{orderColumnArray.map((c) => (
				<TD style={c.headerStyle ? c.headerStyle : undefined}>{cellContent[c.id]}</TD>
			))}
		</TableRow>
	);
}
